import {
  Add,
  ArrowBack,
  Home,
  KeyboardArrowDown,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  KeyboardArrowUp,
  Remove,
  VolumeOff,
  VolumeUp,
} from '@mui/icons-material';
import type { Theme } from '@mui/material';
import { Box, ButtonBase, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { RemoteButton } from '~/generated/graphql';

export type RemoteLayout = 'stacked' | 'split';

interface RemoteControlProps {
  onPress: (button: RemoteButton) => void;
  // `stacked` is a column: menu keys, D-pad, audio bar. `split` divides the same keys between a
  // D-pad and a menu/audio cluster beside it, one per thumb, for a phone held sideways where
  // height is the scarce dimension.
  layout?: RemoteLayout;
  // Mute reaches the player differently depending on the caller (an arbitrary command
  // from the CMS, a translated button from the phone remote), so callers route it.
  onSetMuted: (muted: boolean) => void;
  // Which audio keys the target can act on. See `Device#supports_mute?` / `#supports_volume?`.
  supportsMute: boolean;
  supportsVolume: boolean;
}

// A touch device leaves :hover applied after a tap, so a pressed key stays shaded until
// something else is tapped. Hover feedback is therefore gated on a real pointer; touch
// still gets the :active flash while the finger is down.
const HOVER = '@media (hover: hover)';

// The split frame is always this many times as wide as it is tall, which is what lets its
// sizes below stay in cqw: 1cqw there is 2.6% of its height.
const SPLIT_RATIO = 2.6;

// OK's width as a share of the D-pad box it is centred in, so it covers where the quadrant
// gaps meet at whatever size the ring is drawn.
const OK_WIDTH = '38.7%';

// Sizes are expressed in `cqw` (1% of the container's width) so the remote scales as one unit
// with whatever width the container is given. The stacked values are anchored to the original
// 250px design (e.g. 30px ÷ 250 ≈ 12cqw); the split values to shares of its own height, which
// is the dimension the page has least of in landscape (e.g. a 22%-of-height key is 8.5cqw).
const SIZES = {
  split: {
    dpad: '30cqw',
    dpadIcon: '4.5cqw',
    dpadMargin: '0',
    framePad: '2.7cqw',
    frameRadius: '4.6cqw',
    keyIcon: '3.5cqw',
    label: '2.3cqw',
    labelGap: '1.1cqw',
    menuGap: '3.8cqw',
    menuKey: '8.5cqw',
    okFont: '1.9cqw',
    okRing: '0.7cqw',
    okRingEdge: '0.8cqw',
    pillGlyphPad: '2.3cqw',
    pillHeight: '8.5cqw',
    pillWidth: '38.5cqw',
  },
  stacked: {
    dpad: '100%',
    dpadIcon: '12cqw',
    dpadMargin: '11.2cqw auto',
    framePad: '12.8cqw 9.6cqw',
    frameRadius: '17.6cqw',
    keyIcon: '8cqw',
    label: '4.4cqw',
    labelGap: '2.4cqw',
    menuGap: '9.6cqw',
    menuKey: '19.2cqw',
    okFont: '6.4cqw',
    okRing: '2.4cqw',
    okRingEdge: '2.8cqw',
    pillGlyphPad: '6cqw',
    pillHeight: '17.6cqw',
    pillWidth: '100%',
  },
};

type Sizes = (typeof SIZES)[RemoteLayout];

const keySx = (s: Sizes) => ({
  backgroundColor: 'common.white',
  color: 'text.primary',
  transition: 'background-color 120ms, transform 120ms',
  '& svg': { fontSize: s.keyIcon },
  '&.Mui-focusVisible': { backgroundColor: 'grey.100' },
  [HOVER]: { '&:hover': { backgroundColor: 'grey.100' } },
  '&:active': { backgroundColor: 'grey.200' },
});

const dpadKeySx = (s: Sizes) => ({
  ...keySx(s),
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'center',
  '& svg': { fontSize: s.dpadIcon, transform: 'rotate(-45deg)' },
});

const roundKeySx = (s: Sizes) => ({
  ...keySx(s),
  aspectRatio: '1',
  border: 1,
  borderColor: 'borders.main',
  borderRadius: '50%',
  flexShrink: 0,
  width: s.menuKey,
  '&:active': { backgroundColor: 'grey.200', transform: 'scale(0.94)' },
});

const keyLabelSx = (s: Sizes) => ({
  color: 'text.secondary',
  fontSize: s.label,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
});

// Centred in the D-pad ring, where the rings of background then border colour read as a gap
// between it and the quadrant keys.
const okSx = (s: Sizes) => ({
  aspectRatio: '1',
  backgroundColor: 'primary.dark',
  borderRadius: '50%',
  boxShadow: (theme: Theme) =>
    `0 0 0 ${s.okRing} ${theme.palette.background.default}, 0 0 0 ${s.okRingEdge} ${theme.palette.borders.main}`,
  color: 'primary.contrastText',
  fontSize: s.okFont,
  fontWeight: 500,
  left: '50%',
  letterSpacing: '0.05em',
  position: 'absolute' as const,
  top: '50%',
  transform: 'translate(-50%, -50%)',
  transition: 'background-color 120ms, transform 120ms',
  width: OK_WIDTH,
  '&.Mui-focusVisible': { backgroundColor: 'primary.darkHover' },
  [HOVER]: { '&:hover': { backgroundColor: 'primary.darkHover' } },
  '&:active': { transform: 'translate(-50%, -50%) scale(0.94)' },
});

// The rounded bar the audio keys sit in. Segments are divided generically so the rule holds
// however many of them render.
const pillSx = (s: Sizes) => ({
  alignItems: 'stretch',
  bgcolor: 'common.white',
  border: 1,
  borderColor: 'borders.main',
  borderRadius: 999,
  display: 'flex',
  flexShrink: 0,
  height: s.pillHeight,
  mx: 'auto',
  overflow: 'hidden',
  width: s.pillWidth,
  // Doubled selector for specificity: ButtonBase resets `border: 0` at the same weight, so a
  // single `&` loses the tie and the dividers silently disappear between the key segments.
  '&& > * + *': { borderLeft: (theme: Theme) => `1px solid ${theme.palette.divider}` },
});

// Every dimension above is a share of the remote's width, so its height is too. Callers that
// size the remote against available height need that ratio, and it changes with the layout and
// with whether the audio pill renders — hence derived here, next to the values it depends on,
// rather than hardcoded at the call site where it would silently drift as rows come and go.
// Rounded up slightly: sub-pixel cqw rounding puts the real ratio within ~0.01 either way, and
// over-stating it only costs a few pixels of width, whereas under-stating it overflows.
const ASPECT_BASE = 1.548; // menu row + d-pad + the body's own padding
const ASPECT_PILL = 0.176; // the 17.6cqw audio pill

export const remoteAspect = ({
  layout = 'stacked',
  supportsMute,
  supportsVolume,
}: Pick<RemoteControlProps, 'layout' | 'supportsMute' | 'supportsVolume'>) =>
  layout === 'split'
    ? 1 / SPLIT_RATIO
    : ASPECT_BASE + (supportsVolume || supportsMute ? ASPECT_PILL : 0);

export const RemoteControl = ({
  onPress,
  layout = 'stacked',
  onSetMuted,
  supportsMute,
  supportsVolume,
}: RemoteControlProps) => {
  const { t } = useTranslation();
  const split = layout === 'split';
  const s = SIZES[layout];

  const menuKeys = [
    {
      button: RemoteButton.Back,
      icon: <ArrowBack />,
      label: t('components.remote_control.back'),
    },
    {
      button: RemoteButton.Home,
      icon: <Home />,
      label: t('components.remote_control.home'),
    },
  ];

  const menuRow = (
    <Box sx={{ display: 'flex', gap: s.menuGap, justifyContent: 'center' }}>
      {menuKeys.map(({ button, icon, label }) => (
        <Box
          key={button}
          sx={{ alignItems: 'center', display: 'flex', flexDirection: 'column', gap: s.labelGap }}
        >
          <ButtonBase aria-label={label} onClick={() => onPress(button)} sx={roundKeySx(s)}>
            {icon}
          </ButtonBase>
          <Typography sx={keyLabelSx(s)} variant="caption">
            {label}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  const ok = (
    <ButtonBase onClick={() => onPress(RemoteButton.Ok)} sx={okSx(s)}>
      {t('components.remote_control.ok')}
    </ButtonBase>
  );

  // Clipped because the rotated grid inside is a square as wide as this box, so its bounding box
  // is a further 41% wider. Unclipped, that box counts toward the page's scrollable area and
  // gives the phone remote a horizontal scrollbar. The visible circle is inscribed in this box
  // either way, so nothing on screen is cut.
  const dpad = (
    <Box
      sx={{
        aspectRatio: '1',
        flexShrink: 0,
        margin: s.dpadMargin,
        overflow: 'hidden',
        position: 'relative',
        width: s.dpad,
      }}
    >
      {/* A square grid rotated 45° and clipped to a circle turns its four
          cells into the D-pad ring's quadrant keys. */}
      <Box
        sx={{
          border: 1,
          borderColor: 'borders.main',
          borderRadius: '50%',
          display: 'grid',
          gap: '2px',
          gridTemplateColumns: '1fr 1fr',
          gridTemplateRows: '1fr 1fr',
          inset: 0,
          overflow: 'hidden',
          position: 'absolute',
          transform: 'rotate(45deg)',
        }}
      >
        <ButtonBase
          aria-label={t('components.remote_control.up')}
          onClick={() => onPress(RemoteButton.Up)}
          sx={dpadKeySx(s)}
        >
          <KeyboardArrowUp />
        </ButtonBase>
        <ButtonBase
          aria-label={t('components.remote_control.right')}
          onClick={() => onPress(RemoteButton.Right)}
          sx={dpadKeySx(s)}
        >
          <KeyboardArrowRight />
        </ButtonBase>
        <ButtonBase
          aria-label={t('components.remote_control.left')}
          onClick={() => onPress(RemoteButton.Left)}
          sx={dpadKeySx(s)}
        >
          <KeyboardArrowLeft />
        </ButtonBase>
        <ButtonBase
          aria-label={t('components.remote_control.down')}
          onClick={() => onPress(RemoteButton.Down)}
          sx={dpadKeySx(s)}
        >
          <KeyboardArrowDown />
        </ButtonBase>
      </Box>

      {ok}
    </Box>
  );

  // Audio keys are omitted rather than disabled: a remote with a dead key on it reads as broken,
  // and what the target can't do isn't worth the space. Mute sits between the volume keys, so
  // all four audio controls share one bar.
  const audio = (supportsVolume || supportsMute) && (
    <Box sx={pillSx(s)}>
      {supportsVolume && (
        <ButtonBase
          aria-label={t('components.remote_control.volume_down')}
          onClick={() => onPress(RemoteButton.VolumeDown)}
          sx={{ ...keySx(s), flex: 1 }}
        >
          <Remove />
        </ButtonBase>
      )}

      {/* Always a pair: the player's setMuted takes an absolute boolean rather than
          toggling, so there is no single key that could serve as both. Without it the
          middle is just a speaker glyph, marking the bar as the volume control. */}
      {supportsMute ? (
        <>
          <ButtonBase
            aria-label={t('common.device_commands.mute')}
            onClick={() => onSetMuted(true)}
            sx={{ ...keySx(s), flex: 1 }}
          >
            <VolumeOff />
          </ButtonBase>
          <ButtonBase
            aria-label={t('common.device_commands.unmute')}
            onClick={() => onSetMuted(false)}
            sx={{ ...keySx(s), flex: 1 }}
          >
            <VolumeUp />
          </ButtonBase>
        </>
      ) : (
        <Box
          sx={{
            alignItems: 'center',
            color: 'text.secondary',
            display: 'flex',
            px: s.pillGlyphPad,
          }}
        >
          <VolumeUp sx={{ fontSize: s.keyIcon }} />
        </Box>
      )}

      {supportsVolume && (
        <ButtonBase
          aria-label={t('components.remote_control.volume_up')}
          onClick={() => onPress(RemoteButton.VolumeUp)}
          sx={{ ...keySx(s), flex: 1 }}
        >
          <Add />
        </ButtonBase>
      )}
    </Box>
  );

  return (
    // Establishes the query container; `cqw` inside resolves against this width. The compact
    // width suits the device dialog; the phone page sizes the remote by height instead and
    // overrides this through the class below.
    <Box
      className="fc-remote-root"
      sx={{
        containerType: 'inline-size',
        maxWidth: split ? 'none' : 250,
        mx: 'auto',
        my: 2,
        width: '100%',
      }}
    >
      <Box
        sx={{
          bgcolor: 'background.default',
          border: 1,
          borderColor: 'borders.main',
          borderRadius: s.frameRadius,
          boxShadow: '0 12px 24px -14px rgba(0, 0, 0, 0.3)',
          padding: s.framePad,
          width: '100%',
          ...(split && {
            alignItems: 'center',
            display: 'flex',
            height: '100%',
            // Even gutters around and between the two clusters, which keeps them off the frame
            // edge and closes up on its own when the audio bar is absent.
            justifyContent: 'space-evenly',
          }),
        }}
      >
        {split ? (
          <>
            {dpad}
            {/* The menu keys over the audio bar, opposite the D-pad, so each cluster falls
                under one thumb. */}
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                gap: '2.3cqw',
              }}
            >
              {menuRow}
              {audio}
            </Box>
          </>
        ) : (
          <>
            {menuRow}
            {dpad}
            {audio}
          </>
        )}
      </Box>
    </Box>
  );
};
